import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ResizableMediaOptions, createResizableMediaView, createResizableMediaCommands } from './resizable-media-utils';

declare module '@tiptap/core' {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Commands<ReturnType> {
    resizableVideo: {
      /**
       * Add a resizable video
       */
      setResizableVideo: (options: {
        src: string;
        alt?: string;
        title?: string;
        width?: number;
        align?: string;
        controls?: boolean;
        preload?: string;
      }) => ReturnType;
      /**
       * Update resizable video width
       */
      updateResizableVideoWidth: (width: number) => ReturnType;
      /**
       * Update resizable video alignment
       */
      updateResizableVideoAlign: (align: string) => ReturnType;
    };
  }
}

export const resizableVideoExtension = Node.create<ResizableMediaOptions>({
  name: 'resizable-video',

  addOptions() {
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      HTMLAttributes: {},
      minWidth: 10,
      maxWidth: 100,
    };
  },

  group: 'block',
  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('src'),
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => ({
          src: attributes.src,
        }),
      },
      alt: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('alt'),
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => ({
          alt: attributes.alt,
        }),
      },
      title: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('title'),
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => ({
          title: attributes.title,
        }),
      },
      width: {
        default: 100,
        parseHTML: (element: HTMLElement): number => {
          const width = element.getAttribute('data-width');
          return width ? parseInt(width, 10) : 100;
        },
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => ({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'data-width': attributes.width,
          style: `width: ${attributes.width}%;`,
        }),
      },
      align: {
        default: 'left',
        parseHTML: (element: HTMLElement): string => {
          const align = element.getAttribute('data-align');
          return align || 'left';
        },
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => ({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'data-align': attributes.align,
        }),
      },
      controls: {
        default: true,
        parseHTML: (element: HTMLElement): boolean => element.hasAttribute('controls'),
        renderHTML: (): Record<string, string> => ({
          controls: 'controls',
        }),
      },

      preload: {
        default: 'metadata',
        parseHTML: (element: HTMLElement): string => element.getAttribute('preload') || 'metadata',
        renderHTML: (attributes: Record<string, unknown>): Record<string, unknown> => ({
          preload: attributes.preload,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="resizable-video"]',
        getAttrs: (element: HTMLElement): Record<string, string | number | boolean> | false => {
          const video = (element as HTMLElement).querySelector('video');
          if (!video) {
            return false;
          }

          let width = 100;
          const dataWidth = element.getAttribute('data-width');

          if (dataWidth) {
            width = parseInt(dataWidth, 10);
          } else {
            const style = element.getAttribute('style');
            const widthMatch = style?.match(/width:\s*(\d+)%/);
            if (widthMatch) {
              width = parseInt(widthMatch[1], 10);
            }
          }

          const align = element.getAttribute('data-align') || 'left';

          return {
            src: video.getAttribute('src'),
            alt: video.getAttribute('alt') || '',
            title: video.getAttribute('title') || '',
            width,
            align,
            controls: true,
            preload: video.getAttribute('preload') || 'metadata',
          };
        },
      },
      {
        tag: 'video[src]',
        getAttrs: (element: HTMLElement): Record<string, string | number | boolean> | false => {
          const video = element as HTMLVideoElement;
          return {
            src: video.getAttribute('src'),
            alt: video.getAttribute('alt') || '',
            title: video.getAttribute('title') || '',
            width: 100,
            align: 'left',
            controls: true,
            preload: video.getAttribute('preload') || 'metadata',
          };
        },
      },
    ];
  },

  // eslint-disable-next-line @typescript-eslint/naming-convention
  renderHTML({ HTMLAttributes }): [string, Record<string, unknown>, Record<string, any>] {
    const width = HTMLAttributes['data-width'] || 100;
    const align = HTMLAttributes['data-align'] || 'left';

    let alignStyle = '';
    switch (align) {
      case 'center':
        alignStyle = 'margin: 0 auto;';
        break;
      case 'right':
        alignStyle = 'margin-left: auto;';
        break;
      case 'left':
      default:
        alignStyle = 'margin-right: auto;';
        break;
    }

    return [
      'div',
      mergeAttributes(
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'data-type': 'resizable-video',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'data-width': width,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'data-align': align,
          style: `width: 100%; display: block;`,
        },
        this.options.HTMLAttributes,
      ),
      [
        'video',
        mergeAttributes(
          {
            src: HTMLAttributes.src,
            alt: HTMLAttributes.alt,
            title: HTMLAttributes.title,
            controls: HTMLAttributes.controls ? 'controls' : true,
            preload: HTMLAttributes.preload,
          },
          {
            style: `width: ${width}%; display: block; ${alignStyle}`,
          },
        ),
      ],
    ];
  },

  addNodeView() {
    return createResizableMediaView(
      {
        nodeName: 'video',
        tagName: 'video',
        extraAttributes: {
          controls: true,
          preload: 'metadata',
        },
      },
      this.options,
    );
  },

  addCommands() {
    return createResizableMediaCommands('video');
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('resizable-video'),
        props: {
          nodeViews: {},
        },
      }),
    ];
  },
});

// Type augmentation for commands
declare module '@tiptap/core' {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Commands<ReturnType> {
    resizableVideo: {
      setResizableVideo: (options: {
        src: string;
        alt?: string;
        title?: string;
        width?: number;
        align?: string;
        controls?: boolean;
        preload?: string;
      }) => ReturnType;
      updateResizableVideoWidth: (width: number) => ReturnType;
      updateResizableVideoAlign: (align: string) => ReturnType;
    };
  }
}
